import { forwardRef, type ImgHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

/** Quart de tour, sens horaire (comme CSS `rotate()`). */
export type QuarterTurn = 0 | 90 | 180 | 270;

/**
 * Rapport largeur / hauteur d'un cadre qui montre a l'endroit une image de
 * rapport `imageAspectRatio` tournee de `rotation` : un quart de tour (90 /
 * 270) l'inverse.
 */
export function rotatedAspectRatio(imageAspectRatio: number, rotation: QuarterTurn): number {
  return rotation === 90 || rotation === 270 ? 1 / imageAspectRatio : imageAspectRatio;
}

export interface RotatedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'style'> {
  /** Quart de tour a appliquer a l'image (sens horaire). */
  rotation: QuarterTurn;
  /**
   * Rapport largeur / hauteur du cadre (le parent, en `position: relative`),
   * deja tourne : pour une image 16:9 tournee de 90°, un cadre 9:16. Sert a
   * dimensionner l'image avant rotation pour qu'elle remplisse le cadre.
   */
  frameAspectRatio: number;
}

/**
 * Image tournee d'un quart de tour qui remplit son cadre. Le cadre (parent
 * `relative`, aux bonnes proportions — cf `rotatedAspectRatio`) est fixe par
 * l'appelant ; l'image est posee au centre, dimensionnee comme le cadre avant
 * rotation (largeur et hauteur echangees pour 90 / 270), puis tournee.
 * `object-fit` (`object-contain`, `object-fill`...) passe par `className`.
 */
export const RotatedImage = forwardRef<HTMLImageElement, RotatedImageProps>(
  ({ rotation, frameAspectRatio, className, alt, ...props }, ref) => {
    const quarter = rotation === 90 || rotation === 270;
    // Pourcentages relatifs au cadre : largeur / largeur du cadre, hauteur / hauteur du cadre.
    const width = quarter ? `${100 / frameAspectRatio}%` : '100%';
    const height = quarter ? `${100 * frameAspectRatio}%` : '100%';
    return (
      <img
        ref={ref}
        alt={alt}
        className={cn('absolute left-1/2 top-1/2 max-w-none', className)}
        style={{ width, height, transform: `translate(-50%, -50%) rotate(${rotation}deg)` }}
        data-rotation={rotation}
        {...props}
      />
    );
  }
);

RotatedImage.displayName = 'RotatedImage';
